import React, { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Goal {
  id: string
  name: string
  description?: string
  type: string
  target_value: number
  current_value: number
  progress: number
  unit: string
  start_date: string
  end_date: string
  status: string
  priority: string
  owner?: string
  milestones: string[]
  linked_kpis: string[]
}

interface KPI {
  id: string
  name: string
  description?: string
  category: string
  metric_type: string
  current_value: number
  target_value: number
  unit: string
  trend: string
  change_percentage: number
  period: string
}

interface Milestone {
  id: string
  goal_id: string
  name: string
  target_value: number
  achieved_value: number
  status: string
  due_date: string
  completed_date?: string
}

interface DashboardSummary {
  goals: {
    total: number
    completed: number
    on_track: number
    at_risk: number
    behind: number
    average_progress: number
  }
  kpis: {
    total: number
    on_target: number
    improving: number
    declining: number
  }
  milestones: {
    total: number
    completed: number
    pending: number
    upcoming: number
  }
}

const GoalTrackingPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'goals' | 'kpis' | 'milestones'>('dashboard')
  const [goals, setGoals] = useState<Goal[]>([])
  const [kpis, setKpis] = useState<KPI[]>([])
  const [milestones, setMilestones] = useState<Milestone[]>([])
  const [dashboard, setDashboard] = useState<DashboardSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [showGoalModal, setShowGoalModal] = useState(false)
  const [showKPIModal, setShowKPIModal] = useState(false)
  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null)

  // Form state
  const [goalForm, setGoalForm] = useState({
    name: '',
    description: '',
    type: 'revenue',
    target_value: 0,
    unit: '',
    start_date: '',
    end_date: '',
    priority: 'medium',
    owner: ''
  })

  const [kpiForm, setKpiForm] = useState({
    name: '',
    description: '',
    category: 'revenue',
    metric_type: 'currency',
    target_value: 0,
    unit: '',
    period: 'monthly'
  })

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    try {
      const [dashboardRes, goalsRes, kpisRes] = await Promise.all([
        fetch('/api/goals/dashboard'),
        fetch('/api/goals/'),
        fetch('/api/goals/kpis/list')
      ])

      if (dashboardRes.ok) {
        const data = await dashboardRes.json()
        setDashboard(data)
      }
      if (goalsRes.ok) {
        const data = await goalsRes.json()
        setGoals(data.goals || [])
      }
      if (kpisRes.ok) {
        const data = await kpisRes.json()
        setKpis(data.kpis || [])
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    }
    setLoading(false)
  }

  const handleCreateGoal = async () => {
    try {
      const res = await fetch('/api/goals/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(goalForm)
      })
      if (res.ok) {
        setShowGoalModal(false)
        resetGoalForm()
        fetchData()
      }
    } catch (error) {
      console.error('Error creating goal:', error)
    }
  }

  const handleCreateKPI = async () => {
    try {
      const res = await fetch('/api/goals/kpis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(kpiForm)
      })
      if (res.ok) {
        setShowKPIModal(false)
        resetKPIForm()
        fetchData()
      }
    } catch (error) {
      console.error('Error creating KPI:', error)
    }
  }

  const handleDeleteGoal = async (goalId: string) => {
    if (!confirm('Are you sure you want to delete this goal?')) return
    try {
      const res = await fetch(`/api/goals/${goalId}`, { method: 'DELETE' })
      if (res.ok) {
        fetchData()
      }
    } catch (error) {
      console.error('Error deleting goal:', error)
    }
  }

  const handleDeleteKPI = async (kpiId: string) => {
    if (!confirm('Are you sure you want to delete this KPI?')) return
    try {
      const res = await fetch(`/api/goals/kpis/${kpiId}`, { method: 'DELETE' })
      if (res.ok) {
        fetchData()
      }
    } catch (error) {
      console.error('Error deleting KPI:', error)
    }
  }

  const fetchGoalMilestones = async (goalId: string) => {
    try {
      const res = await fetch(`/api/goals/${goalId}/milestones`)
      if (res.ok) {
        const data = await res.json()
        setMilestones(data.milestones || [])
      }
    } catch (error) {
      console.error('Error fetching milestones:', error)
    }
  }

  const resetGoalForm = () => {
    setGoalForm({
      name: '',
      description: '',
      type: 'revenue',
      target_value: 0,
      unit: '',
      start_date: '',
      end_date: '',
      priority: 'medium',
      owner: ''
    })
  }

  const resetKPIForm = () => {
    setKpiForm({
      name: '',
      description: '',
      category: 'revenue',
      metric_type: 'currency',
      target_value: 0,
      unit: '',
      period: 'monthly'
    })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#28a745'
      case 'on_track': return '#4A90E2'
      case 'at_risk': return '#ffc107'
      case 'behind': return '#dc3545'
      default: return '#6c757d'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return '#dc3545'
      case 'medium': return '#ffc107'
      case 'low': return '#28a745'
      default: return '#6c757d'
    }
  }

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return '📈'
      case 'down': return '📉'
      default: return '➡️'
    }
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      <Header />
      <main style={{ padding: '32px', maxWidth: '1600px', margin: '0 auto' }}>
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#1a1a2e', marginBottom: '8px' }}>
            Goal Tracking & KPIs
          </h1>
          <p style={{ color: '#64748b' }}>
            Set goals, track progress, and monitor key performance indicators
          </p>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
          {[
            { id: 'dashboard', label: 'Dashboard' },
            { id: 'goals', label: 'Goals' },
            { id: 'kpis', label: 'KPIs' },
            { id: 'milestones', label: 'Milestones' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              style={{
                padding: '10px 20px',
                border: 'none',
                background: activeTab === tab.id ? '#4A90E2' : 'transparent',
                color: activeTab === tab.id ? 'white' : '#64748b',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 500
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>Loading...</div>
        ) : (
          <>
            {/* Dashboard Tab */}
            {activeTab === 'dashboard' && dashboard && (
              <div>
                {/* Summary Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                  <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <h3 style={{ fontSize: '14px', color: '#64748b', marginBottom: '12px' }}>Goals Progress</h3>
                    <div style={{ fontSize: '36px', fontWeight: 700, color: '#1a1a2e', marginBottom: '8px' }}>
                      {dashboard.goals.average_progress.toFixed(1)}%
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', fontSize: '13px' }}>
                      <div><span style={{ color: '#28a745' }}>Completed:</span> {dashboard.goals.completed}</div>
                      <div><span style={{ color: '#4A90E2' }}>On Track:</span> {dashboard.goals.on_track}</div>
                      <div><span style={{ color: '#ffc107' }}>At Risk:</span> {dashboard.goals.at_risk}</div>
                      <div><span style={{ color: '#dc3545' }}>Behind:</span> {dashboard.goals.behind}</div>
                    </div>
                  </div>

                  <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <h3 style={{ fontSize: '14px', color: '#64748b', marginBottom: '12px' }}>KPI Performance</h3>
                    <div style={{ fontSize: '36px', fontWeight: 700, color: '#28a745', marginBottom: '8px' }}>
                      {dashboard.kpis.on_target}/{dashboard.kpis.total}
                    </div>
                    <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>On Target</div>
                    <div style={{ display: 'flex', gap: '16px', marginTop: '8px', fontSize: '13px' }}>
                      <div><span style={{ color: '#28a745' }}>📈 Improving:</span> {dashboard.kpis.improving}</div>
                      <div><span style={{ color: '#dc3545' }}>📉 Declining:</span> {dashboard.kpis.declining}</div>
                    </div>
                  </div>

                  <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <h3 style={{ fontSize: '14px', color: '#64748b', marginBottom: '12px' }}>Milestones</h3>
                    <div style={{ fontSize: '36px', fontWeight: 700, color: '#4A90E2', marginBottom: '8px' }}>
                      {dashboard.milestones.completed}/{dashboard.milestones.total}
                    </div>
                    <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>Completed</div>
                    <div style={{ marginTop: '8px', fontSize: '13px' }}>
                      <span style={{ color: '#ffc107' }}>⏰ Upcoming (7 days):</span> {dashboard.milestones.upcoming}
                    </div>
                  </div>
                </div>

                {/* Recent Goals */}
                <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>Active Goals</h3>
                  <div style={{ display: 'grid', gap: '12px' }}>
                    {goals.filter(g => g.status !== 'completed').slice(0, 5).map(goal => (
                      <div
                        key={goal.id}
                        style={{
                          padding: '16px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          borderLeft: `4px solid ${getStatusColor(goal.status)}`
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <div>
                            <span style={{ fontWeight: 600 }}>{goal.name}</span>
                            <span style={{
                              marginLeft: '8px',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              background: getStatusColor(goal.status) + '20',
                              color: getStatusColor(goal.status),
                              textTransform: 'capitalize'
                            }}>
                              {goal.status.replace('_', ' ')}
                            </span>
                          </div>
                          <span style={{ fontSize: '18px', fontWeight: 700 }}>{goal.progress.toFixed(1)}%</span>
                        </div>
                        <div style={{ background: '#e2e8f0', borderRadius: '4px', height: '8px' }}>
                          <div style={{
                            width: `${goal.progress}%`,
                            height: '100%',
                            background: getStatusColor(goal.status),
                            borderRadius: '4px',
                            transition: 'width 0.5s'
                          }} />
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '8px' }}>
                          {goal.current_value.toLocaleString()} / {goal.target_value.toLocaleString()} {goal.unit}
                          <span style={{ float: 'right' }}>Due: {new Date(goal.end_date).toLocaleDateString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* KPIs Overview */}
                <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>Key Performance Indicators</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px' }}>
                    {kpis.slice(0, 6).map(kpi => (
                      <div
                        key={kpi.id}
                        style={{
                          padding: '16px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px'
                        }}
                      >
                        <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>{kpi.name}</div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                          <span style={{ fontSize: '24px', fontWeight: 700 }}>{kpi.current_value}</span>
                          <span style={{ fontSize: '14px', color: '#64748b' }}>{kpi.unit}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                          <span style={{ fontSize: '18px' }}>{getTrendIcon(kpi.trend)}</span>
                          <span style={{
                            fontSize: '13px',
                            color: kpi.change_percentage > 0 ? '#28a745' : kpi.change_percentage < 0 ? '#dc3545' : '#64748b'
                          }}>
                            {kpi.change_percentage > 0 ? '+' : ''}{kpi.change_percentage}%
                          </span>
                        </div>
                        <div style={{
                          marginTop: '8px',
                          fontSize: '12px',
                          color: kpi.current_value >= kpi.target_value ? '#28a745' : '#64748b'
                        }}>
                          Target: {kpi.target_value}{kpi.unit}
                          {kpi.current_value >= kpi.target_value && ' ✓'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Goals Tab */}
            {activeTab === 'goals' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h2 style={{ fontSize: '18px', fontWeight: 600 }}>Goals</h2>
                  <button
                    onClick={() => { resetGoalForm(); setShowGoalModal(true); }}
                    style={{
                      padding: '10px 20px',
                      background: '#4A90E2',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 500
                    }}
                  >
                    + Create Goal
                  </button>
                </div>

                <div style={{ display: 'grid', gap: '16px' }}>
                  {goals.map(goal => (
                    <div
                      key={goal.id}
                      style={{
                        background: 'white',
                        borderRadius: '12px',
                        padding: '20px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        borderLeft: `4px solid ${getStatusColor(goal.status)}`
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                            <h3 style={{ fontSize: '16px', fontWeight: 600, margin: 0 }}>{goal.name}</h3>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              background: getStatusColor(goal.status) + '20',
                              color: getStatusColor(goal.status),
                              textTransform: 'capitalize'
                            }}>
                              {goal.status.replace('_', ' ')}
                            </span>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              background: getPriorityColor(goal.priority) + '20',
                              color: getPriorityColor(goal.priority),
                              textTransform: 'capitalize'
                            }}>
                              {goal.priority} priority
                            </span>
                          </div>
                          {goal.description && (
                            <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '12px' }}>{goal.description}</p>
                          )}
                          <div style={{ marginBottom: '8px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                              <span style={{ fontSize: '14px' }}>Progress</span>
                              <span style={{ fontSize: '14px', fontWeight: 600 }}>{goal.progress.toFixed(1)}%</span>
                            </div>
                            <div style={{ background: '#e2e8f0', borderRadius: '4px', height: '8px' }}>
                              <div style={{
                                width: `${goal.progress}%`,
                                height: '100%',
                                background: getStatusColor(goal.status),
                                borderRadius: '4px'
                              }} />
                            </div>
                          </div>
                          <div style={{ fontSize: '13px', color: '#64748b' }}>
                            <span>{goal.current_value.toLocaleString()} / {goal.target_value.toLocaleString()} {goal.unit}</span>
                            <span style={{ marginLeft: '16px' }}>
                              {new Date(goal.start_date).toLocaleDateString()} - {new Date(goal.end_date).toLocaleDateString()}
                            </span>
                            {goal.owner && <span style={{ marginLeft: '16px' }}>Owner: {goal.owner}</span>}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', marginLeft: '16px' }}>
                          <button
                            onClick={() => { setSelectedGoal(goal); fetchGoalMilestones(goal.id); setActiveTab('milestones'); }}
                            style={{
                              padding: '6px 12px',
                              border: '1px solid #e2e8f0',
                              background: 'white',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '13px'
                            }}
                          >
                            Milestones
                          </button>
                          <button
                            onClick={() => handleDeleteGoal(goal.id)}
                            style={{
                              padding: '6px 12px',
                              border: '1px solid #dc3545',
                              background: 'white',
                              color: '#dc3545',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '13px'
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {goals.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '60px', color: '#64748b', background: 'white', borderRadius: '12px' }}>
                      No goals created yet. Create your first goal to start tracking.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* KPIs Tab */}
            {activeTab === 'kpis' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h2 style={{ fontSize: '18px', fontWeight: 600 }}>Key Performance Indicators</h2>
                  <button
                    onClick={() => { resetKPIForm(); setShowKPIModal(true); }}
                    style={{
                      padding: '10px 20px',
                      background: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 500
                    }}
                  >
                    + Add KPI
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                  {kpis.map(kpi => (
                    <div
                      key={kpi.id}
                      style={{
                        background: 'white',
                        borderRadius: '12px',
                        padding: '20px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                        <div>
                          <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>{kpi.name}</div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>
                            <span style={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: '4px' }}>{kpi.category}</span>
                            <span style={{ marginLeft: '8px' }}>{kpi.period}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteKPI(kpi.id)}
                          style={{
                            padding: '4px 8px',
                            border: '1px solid #dc3545',
                            background: 'white',
                            color: '#dc3545',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Delete
                        </button>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '32px', fontWeight: 700 }}>{kpi.current_value}</span>
                        <span style={{ fontSize: '16px', color: '#64748b' }}>{kpi.unit}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                        <span style={{ fontSize: '24px' }}>{getTrendIcon(kpi.trend)}</span>
                        <span style={{
                          fontSize: '16px',
                          fontWeight: 600,
                          color: kpi.change_percentage > 0 ? '#28a745' : kpi.change_percentage < 0 ? '#dc3545' : '#64748b'
                        }}>
                          {kpi.change_percentage > 0 ? '+' : ''}{kpi.change_percentage}%
                        </span>
                      </div>
                      <div style={{ marginBottom: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                          <span>Progress to Target</span>
                          <span>{Math.min(100, (kpi.current_value / kpi.target_value * 100)).toFixed(0)}%</span>
                        </div>
                        <div style={{ background: '#e2e8f0', borderRadius: '4px', height: '6px' }}>
                          <div style={{
                            width: `${Math.min(100, (kpi.current_value / kpi.target_value * 100))}%`,
                            height: '100%',
                            background: kpi.current_value >= kpi.target_value ? '#28a745' : '#4A90E2',
                            borderRadius: '4px'
                          }} />
                        </div>
                      </div>
                      <div style={{ fontSize: '13px', color: '#64748b' }}>
                        Target: {kpi.target_value}{kpi.unit}
                        {kpi.current_value >= kpi.target_value && <span style={{ color: '#28a745', marginLeft: '8px' }}>✓ On Target</span>}
                      </div>
                    </div>
                  ))}
                  {kpis.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '60px', color: '#64748b', background: 'white', borderRadius: '12px', gridColumn: '1 / -1' }}>
                      No KPIs defined yet. Add a KPI to start monitoring performance.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Milestones Tab */}
            {activeTab === 'milestones' && (
              <div>
                <div style={{ marginBottom: '16px' }}>
                  <h2 style={{ fontSize: '18px', fontWeight: 600 }}>
                    Milestones {selectedGoal && <span style={{ fontWeight: 400, color: '#64748b' }}>for {selectedGoal.name}</span>}
                  </h2>
                </div>

                <div style={{ display: 'grid', gap: '12px' }}>
                  {milestones.map(milestone => (
                    <div
                      key={milestone.id}
                      style={{
                        background: 'white',
                        borderRadius: '12px',
                        padding: '16px 20px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        borderLeft: `4px solid ${milestone.status === 'completed' ? '#28a745' : milestone.status === 'in_progress' ? '#4A90E2' : '#64748b'}`
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                            <span style={{ fontWeight: 600 }}>{milestone.name}</span>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              background: milestone.status === 'completed' ? '#28a74520' : milestone.status === 'in_progress' ? '#4A90E220' : '#64748b20',
                              color: milestone.status === 'completed' ? '#28a745' : milestone.status === 'in_progress' ? '#4A90E2' : '#64748b',
                              textTransform: 'capitalize'
                            }}>
                              {milestone.status.replace('_', ' ')}
                            </span>
                          </div>
                          <div style={{ fontSize: '13px', color: '#64748b' }}>
                            Progress: {milestone.achieved_value} / {milestone.target_value}
                            <span style={{ marginLeft: '16px' }}>Due: {new Date(milestone.due_date).toLocaleDateString()}</span>
                            {milestone.completed_date && (
                              <span style={{ marginLeft: '16px', color: '#28a745' }}>
                                Completed: {new Date(milestone.completed_date).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        {milestone.status === 'completed' && (
                          <span style={{ fontSize: '24px' }}>✓</span>
                        )}
                      </div>
                    </div>
                  ))}
                  {milestones.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '60px', color: '#64748b', background: 'white', borderRadius: '12px' }}>
                      {selectedGoal ? 'No milestones for this goal yet.' : 'Select a goal to view its milestones.'}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* Goal Modal */}
        {showGoalModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              width: '500px',
              maxHeight: '80vh',
              overflow: 'auto'
            }}>
              <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '20px' }}>Create Goal</h2>

              <div style={{ display: 'grid', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}>Goal Name</label>
                  <input
                    type="text"
                    value={goalForm.name}
                    onChange={e => setGoalForm({ ...goalForm, name: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
                    placeholder="e.g., Q1 Revenue Target"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}>Description</label>
                  <textarea
                    value={goalForm.description}
                    onChange={e => setGoalForm({ ...goalForm, description: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', minHeight: '60px' }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}>Type</label>
                    <select
                      value={goalForm.type}
                      onChange={e => setGoalForm({ ...goalForm, type: e.target.value })}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                    >
                      <option value="revenue">Revenue</option>
                      <option value="conversion">Conversion</option>
                      <option value="expansion">Expansion</option>
                      <option value="operations">Operations</option>
                      <option value="customer">Customer</option>
                      <option value="growth">Growth</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}>Priority</label>
                    <select
                      value={goalForm.priority}
                      onChange={e => setGoalForm({ ...goalForm, priority: e.target.value })}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}>Target Value</label>
                    <input
                      type="number"
                      value={goalForm.target_value}
                      onChange={e => setGoalForm({ ...goalForm, target_value: parseFloat(e.target.value) || 0 })}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}>Unit</label>
                    <input
                      type="text"
                      value={goalForm.unit}
                      onChange={e => setGoalForm({ ...goalForm, unit: e.target.value })}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
                      placeholder="e.g., GBP, %, markets"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}>Start Date</label>
                    <input
                      type="date"
                      value={goalForm.start_date}
                      onChange={e => setGoalForm({ ...goalForm, start_date: e.target.value })}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}>End Date</label>
                    <input
                      type="date"
                      value={goalForm.end_date}
                      onChange={e => setGoalForm({ ...goalForm, end_date: e.target.value })}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}>Owner</label>
                  <input
                    type="text"
                    value={goalForm.owner}
                    onChange={e => setGoalForm({ ...goalForm, owner: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
                    placeholder="Team or person responsible"
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowGoalModal(false)}
                  style={{ padding: '10px 20px', border: '1px solid #e2e8f0', background: 'white', borderRadius: '8px', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateGoal}
                  style={{ padding: '10px 20px', border: 'none', background: '#4A90E2', color: 'white', borderRadius: '8px', cursor: 'pointer', fontWeight: 500 }}
                >
                  Create Goal
                </button>
              </div>
            </div>
          </div>
        )}

        {/* KPI Modal */}
        {showKPIModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              width: '450px'
            }}>
              <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '20px' }}>Add KPI</h2>

              <div style={{ display: 'grid', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}>KPI Name</label>
                  <input
                    type="text"
                    value={kpiForm.name}
                    onChange={e => setKpiForm({ ...kpiForm, name: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
                    placeholder="e.g., Monthly Revenue"
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}>Category</label>
                    <select
                      value={kpiForm.category}
                      onChange={e => setKpiForm({ ...kpiForm, category: e.target.value })}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                    >
                      <option value="revenue">Revenue</option>
                      <option value="conversion">Conversion</option>
                      <option value="customer">Customer</option>
                      <option value="operations">Operations</option>
                      <option value="expansion">Expansion</option>
                      <option value="marketing">Marketing</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}>Period</label>
                    <select
                      value={kpiForm.period}
                      onChange={e => setKpiForm({ ...kpiForm, period: e.target.value })}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}>Target Value</label>
                    <input
                      type="number"
                      value={kpiForm.target_value}
                      onChange={e => setKpiForm({ ...kpiForm, target_value: parseFloat(e.target.value) || 0 })}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}>Unit</label>
                    <input
                      type="text"
                      value={kpiForm.unit}
                      onChange={e => setKpiForm({ ...kpiForm, unit: e.target.value })}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
                      placeholder="e.g., GBP, %, /5"
                    />
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowKPIModal(false)}
                  style={{ padding: '10px 20px', border: '1px solid #e2e8f0', background: 'white', borderRadius: '8px', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateKPI}
                  style={{ padding: '10px 20px', border: 'none', background: '#28a745', color: 'white', borderRadius: '8px', cursor: 'pointer', fontWeight: 500 }}
                >
                  Add KPI
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}

export default GoalTrackingPage
